import { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { AccountContext } from '@/context/account.context';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import useMixpanel from '@/hooks/useMixpanel';
import Submission from '@/utils/api/odas/submission';
import { isSuccessResponse } from '@/constants/httpStatus';
import t from '@/utils/Language/SuggestEdits-translations';

// The pro reminder emails ask "is this information still correct?" and link to the resource
// page with ?info=correct|incorrect&source=pro_email, and resource cards' "Suggest edits"
// link opens it with ?showEditResourceModal=true. The Angular resource page handled these
// params (controllers/resources/show.js); this ports them for the React resource pages.
// Pass the untranslated (SSR) resource: verification PUTs the resource back as the
// submission content, and a translated payload must never be written into a submission.
const useResourceQueryParamActions = (resource) => {
  const account = useContext(AccountContext);
  const modal = useContext(modalContext);
  const { addToast } = useContext(toastContext);
  const { track } = useMixpanel();
  const router = useRouter();

  const stripQueryParams = (keys) => {
    const query = { ...router.query };
    keys.forEach((key) => delete query[key]);
    router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  const submitVerification = async (submissionSource) => {
    let response;
    try {
      response = await Submission.verifyResource({ resource, submissionSource });
    } catch (error) {
      // The submission was never sent -- the resource could not be loaded to build it.
      addToast({ message: t('There was a problem with your submission_'), type: 'error' });
      return;
    }
    // The API wrapper returns the axios error object instead of throwing, so a failure's
    // status lives at response.response.status.
    const status = response?.status || response?.response?.status;
    if (isSuccessResponse(status)) {
      // Verify can also be confirmed from the ⋯ menu (see MoreActionsDropdown); here the raw URL
      // source (e.g. 'pro_email') is passed so the email-origin verifies are distinguishable.
      track('Verified information', { source: submissionSource, resource_id: resource.id, resource_type: resource.resource_type });
      addToast({ message: t('Thank you for verifying that this information is correct!'), type: 'success' });
      stripQueryParams(['info', 'source']);
    } else if (status !== 401) {
      // A logged-out member's 401 fails silently rather than toasting, matching Angular.
      addToast({ message: t('There was a problem with your submission_'), type: 'error' });
    }
  };

  useEffect(() => {
    if (!router.isReady || !resource?.id) return;
    const { info, source, referrer, showEditResourceModal } = router.query;
    // Collected instead of stripped inline: multiple router.replace calls in the same tick
    // each read router.query fresh, so the last one wins and can undo an earlier strip. One
    // batched call at the end avoids that race.
    const paramsToStrip = [];

    if (source === 'pro_email_link') {
      track('Clicked link', { source: 'Automated email', category: 'Email engagement' });
      paramsToStrip.push('source');
    }

    if (showEditResourceModal) {
      // Pass the URL source through so the submission records its true origin (Angular read
      // $location.search().source); it's only 'pro_email' when the link came from a pro email.
      modal.enqueueModal({ name: 'SuggestEditModal', resource, submissionSource: source });
      paramsToStrip.push('showEditResourceModal');
    }

    if (info === 'incorrect') {
      if (source === 'pro_email') {
        track('Clicked button', { source: 'Automated email', category: 'Email engagement', value: 'no' });
      }
      modal.enqueueModal({ name: 'InaccurateInformationModal', resource, submissionSource: source });
      paramsToStrip.push('info', 'source');
    }

    if (info === 'correct') {
      if (source === 'pro_email') {
        track('Clicked button', { source: 'Automated email', category: 'Email engagement', value: 'yes' });
      }
      // A member sent to /confirm-login who came back without logging in arrives with this
      // referrer; drop the param instead of retrying a submission that would 401.
      if (referrer === 'confirm-login' && !account?.state?.isLoggedIn) {
        paramsToStrip.push('info');
      } else if (resource.has_pending_submission) {
        paramsToStrip.push('info', 'source');
      } else if (account?.state?.isLoggedIn) {
        submitVerification(source);
      } else {
        // Not logged in yet (and didn't just decline at /confirm-login): prompt to log in,
        // then submit the verification once authenticated -- mirrors Angular's flow instead
        // of attempting the submit up front and relying on the 401 to fail silently.
        modal.enqueueModal(
          { name: 'LoginModal', reason: 'verifyInformation', onAuthenticated: () => submitVerification(source) },
          { forceOpen: true }
        );
      }
    }

    if (paramsToStrip.length) {
      stripQueryParams(paramsToStrip);
    }
  }, [router.isReady, resource?.id]);

  const invitationPromptedRef = useRef(false);

  // Invitation links (?invitation_id=...) offer an optional signup prompt to a member who
  // isn't logged in yet. Wait for isSessionRetrieved so a member who is logged in (but whose
  // session hasn't resolved yet) never sees it flash. The param is deliberately never
  // stripped -- the signup flow itself reads invitation_id/invitation_token (useAccount.js).
  useEffect(() => {
    if (invitationPromptedRef.current) return;
    if (!router.isReady || !router.query.invitation_id) return;
    if (!account?.isSessionRetrieved || account?.state?.isLoggedIn) return;
    invitationPromptedRef.current = true;
    modal.enqueueModal({ name: 'SignupModal' }, { forceOpen: true });
  }, [router.isReady, router.query.invitation_id, account?.isSessionRetrieved, account?.state?.isLoggedIn]);
};

export default useResourceQueryParamActions;
